import gql from "graphql-tag";
import { productCardPath, toCanonicalInternalPath } from "./canonicalPath";
import { getCachedProduct, prefetchProduct } from "./productCache";

// Card data is enough to open a PDP. Keep the exact backend id/path pair in a
// bounded browser-only registry, including links on home, search and info pages.
const links = new Map<string, { product: any; created: number }>();
const pending = new Map<string, Promise<any>>();
const TTL = 5 * 60 * 1000;
const MAX_LINKS = 128;
const validId = (id: unknown) =>
  /^\d+$/.test(String(id || "")) && Number(id) > 0;

export function registerProductLink(product: any): void {
  if (typeof window === "undefined" || !validId(product?.id)) return;
  const path = productCardPath(product);
  links.delete(path);
  links.set(path, { product, created: Date.now() });
  while (links.size > MAX_LINKS) links.delete(links.keys().next().value!);
}

export function getProductLink(path: string): any | null {
  if (typeof window === "undefined") return null;
  const key = toCanonicalInternalPath(path);
  const entry = key && links.get(key);
  if (!entry) return null;
  if (Date.now() - entry.created > TTL) {
    links.delete(key);
    return null;
  }
  return entry.product;
}

export function prepareProductNavigation(store: any, product: any): boolean {
  if (typeof window === "undefined" || !validId(product?.id)) return false;
  const path = productCardPath(product);
  registerProductLink(product);
  const cached = getCachedProduct(product.id);
  const {
    modelVariants: _variants,
    __ssrComplete: _ssr,
    ...preview
  } = cached || product;
  store.commit("store/product/setProduct", {
    ...preview,
    url: path,
    __productNavigationPath: path,
    // Partial list data must never enable purchase before options/stock arrive.
    __productPreview: !cached,
  });
  void prefetchProduct(store, product.id);
  return true;
}

const PRODUCT_PATH_QUERY = gql`
  query ($url: String) {
    searchUrl(url: $url) {
      type
      id
    }
  }
`;

// Editorial product links may have only a URL. Resolve those on user intent,
// then share the same product request/cache. Warming never changes the page.
export function warmProductLink(store: any, href: string): Promise<any> {
  if (typeof window === "undefined") return Promise.resolve(null);
  const path = toCanonicalInternalPath(href);
  if (!path) return Promise.resolve(null);
  const known = getProductLink(path);
  if (known) return prefetchProduct(store, known.id);
  const existing = pending.get(path);
  if (existing) return existing;
  const numeric = path.match(/^\/store\/product\/(\d+)$/);
  const resolve = numeric
    ? Promise.resolve({ searchUrl: { type: "product", id: numeric[1] } })
    : store.dispatch("apollo/query", {
        query: PRODUCT_PATH_QUERY,
        variables: { url: path },
      });
  const request = resolve
    .then(async (data: any) => {
      const route = data?.searchUrl;
      if (route?.type !== "product" || !validId(route.id)) return null;
      const product = await prefetchProduct(store, route.id);
      if (product && String(product.id) === String(route.id)) {
        registerProductLink({ ...product, url: path });
      }
      return product;
    })
    .catch(() => null)
    .finally(() => pending.delete(path));
  pending.set(path, request);
  return request;
}
